import React from "react";
import { ReactElement } from "react-markdown/lib/react-markdown";
import { HypotheekBedragInBox1, FiscaleAftrekHoofdstuk } from "../../../../../.generated/rapportage/rapportagetypes";
import { Paragraph } from "../../../components/Paragraph";
import Table from "../../../components/Table";
import { Column } from "../../../components/BaseTable";
import Heading from "../../../components/Heading";

import "./fiscale-aftrek.scss";
import { formatCurrency } from "../../../../formatters";
import defaultTheme from "../../../../../styles/themes/default";

type FiscaleAftrekProps = {
  fiscaleAftrek: FiscaleAftrekHoofdstuk;
};

const box1Table = (hoofdstuk: FiscaleAftrekHoofdstuk): ReactElement => {
  const columns: Column<HypotheekBedragInBox1>[] = [
    {
      header: "Hypotheekbedrag in Box 1",
      accessor: () => "",
      align: "left",
    },
    {
      header: "Koopsom woning",
      accessor: (row: HypotheekBedragInBox1) => row.investering?.koopsom ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "Bouwrente over de periode voor aankoop",
      accessor: (row: HypotheekBedragInBox1) => row.investering?.bouwrente ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "Meerwerk",
      accessor: (row: HypotheekBedragInBox1) => row.investering?.meerwerk ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "Verbouwing",
      accessor: (row: HypotheekBedragInBox1) => row.investering?.verbouwing ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "Overdrachtsbelasting",
      accessor: (row: HypotheekBedragInBox1) => row.investering?.overdrachtsbelasting ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "Makelaarscourtage",
      accessor: (row: HypotheekBedragInBox1) => row.investering?.makelaarscourtage ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "Leveringsakte",
      accessor: (row: HypotheekBedragInBox1) => row.investering?.leveringsakte ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "Bankgarantie",
      accessor: (row: HypotheekBedragInBox1) => row.investering?.kostenBankgarantieBedrag ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "Investering",
      accessor: (row: HypotheekBedragInBox1) => [formatCurrency(row.investering?.totaal), "+"],
      dataType: "currency",
      align: "left",
      className: "sum-cell bold",
    },
    {
      header: "Eigenwoningreserve en vervreemdingssaldo",
      accessor: (row: HypotheekBedragInBox1) => [
        formatCurrency(row.investering?.eigenwoningreserve),
        "-/-",
      ],
      align: "left",
      dataType: "currency",
    },
    {
      header: "Doorlopende hypothe(e)k(en) box 1",
      accessor: (row: HypotheekBedragInBox1) => [
        formatCurrency(row.investering?.doorlopendeLeningdelen),
        "-/-",
      ],
      align: "left",
      dataType: "currency",
    },
    {
      header: "Advieskosten",
      accessor: (row: HypotheekBedragInBox1) => row.financieringskosten?.advieskosten ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "NHG-kosten",
      accessor: (row: HypotheekBedragInBox1) => row.financieringskosten?.nhgKosten ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "Kosten hypotheekakte",
      accessor: (row: HypotheekBedragInBox1) => row.financieringskosten?.hypotheekakte ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "Arbeidskosten notaris",
      accessor: (row: HypotheekBedragInBox1) => row.financieringskosten?.arbeidskostenNotaris ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "Bereidstellingskosten",
      accessor: (row: HypotheekBedragInBox1) => row.financieringskosten?.bereidstellingskosten ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "Taxatiekosten ",
      accessor: (row: HypotheekBedragInBox1) => row.financieringskosten?.taxatiekosten ?? null,
      align: "left",
      dataType: "currency",
    },
    {
      header: "Financieringskosten ",
      accessor: (row: HypotheekBedragInBox1) => [
        formatCurrency(row.financieringskosten?.totaal),
        "+",
      ],
      dataType: "currency",
      align: "left",
      className: "sum-cell bold",
    },
    {
      header: "Waarvan niet aftrekbaar (Box 3)",
      accessor: (row: any) => [
        formatCurrency(row.totalen?.waarvanNietAftrekbaarBox3),
        "-/-",
      ],
      align: "left",
      dataType: "currency",
      className: "sum-cell bold",
    },
    {
      header: "Maximaal te financieren in Box 1 ",
      accessor: (row: HypotheekBedragInBox1) => row.totalen?.maximaalTeFinancierenInBox1 ?? null,
      align: "left",
      dataType: "currency",
      className: "sum-cell bold",
    },
    {
      header: "Gewenste hypotheek (incl. andere financieringen)",
      accessor: (row: HypotheekBedragInBox1) => row.totalen?.gewensteHypotheekInclAndereFinancieringen ?? null,
      align: "left",
      dataType: "currency",
      className: "sum-cell bold",
    },
    {
      header: "Hypotheekbedrag in Box 1",
      accessor: (row: HypotheekBedragInBox1) => row.totalen?.hypotheekInBox1 ?? null,
      align: "left",
      dataType: "currency",
      className: "sum-cell bold",
    },
  ];

  const data = hoofdstuk.hypotheekBedragInBox1 ? [hoofdstuk.hypotheekBedragInBox1] : [];

  return (
    <Table
      rotate
      autoAlignCurrency
      columns={columns}
      data={data}
      id="box1-table"
      className="labels-right"
      
    />
  );
};

const eenmaligeBelastingteruggaveTable = (
  hoofdstuk: FiscaleAftrekHoofdstuk
): ReactElement => {
  if (!hoofdstuk.belastingteruggave) {
    return <></>;
  }

  const columns: Column[] = [
    {
      header: "Indicatieve eenmalige belastingteruggave",
      accessor: () => "",
      align: "left",
    },
    {
      header: "Totaal fiscaal aftrekbare bedragen",
      accessor: "totaalFiscaalAftrekbareBedragen",
      align: "left",
      dataType: "currency",
    },
    {
      header: "Eenmalige extra belastingteruggave ",
      accessor: "eenmaligeExtraBelastingteruggave",
      align: "left",
      dataType: "currency",
    },
  ];

  const data = [hoofdstuk.belastingteruggave || {}];

  return (
    <Table
      rotate
      autoAlignCurrency
      columns={columns}
      data={data}
      id="belastingteruggave-table"
      className="labels-right"
      
    />
  );
};

export const FiscaleAftrekChapter = ({
  fiscaleAftrek,
}: FiscaleAftrekProps): ReactElement => {
  return (
    <div className="break-avoid">
      {fiscaleAftrek.titel && (
        <Heading text={`${fiscaleAftrek.titel}`} level={2}></Heading>
      )}

      {fiscaleAftrek.paragrafen?.p3040501 && (
        <Paragraph paragraaf={fiscaleAftrek.paragrafen?.p3040501}></Paragraph>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: defaultTheme.tableGap,
        }}
      >
        <div style={{ flex:1 }}>{box1Table(fiscaleAftrek)}</div>
        {/* <div style={{ flex:1 }}></div> */}
      </div>

      <br />
      {fiscaleAftrek.paragrafen?.p3040502 && (
        <Paragraph paragraaf={fiscaleAftrek.paragrafen?.p3040502}></Paragraph>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: defaultTheme.tableGap,
        }}
      >
          <div style={{ flex:1 }}>{eenmaligeBelastingteruggaveTable(fiscaleAftrek)}</div>
          {/* <div style={{ flex:1 }}></div> */}
      </div>
    </div>
  );
};
